/**
 * Pluggable player actions.
 *
 * An Action is a self-contained unit of behaviour bound to an action id (the
 * same id used as the key in Intent.pulses). The simulation runs every
 * registered action against every entity each step. Adding a brand-new action
 * later (split, eject, dash, ...) is: write a new Action, register it. The core
 * loop and the entity model do not change.
 */
import type { GameConfig } from '../../config/config';
import type { Entity } from '../types';

export interface ActionContext {
  entity: Entity;
  cfg: GameConfig;
  dt: number;
  /** Current simulation time in seconds (for cooldowns, etc.). */
  now: number;
}

export interface Action {
  /** Must match the key used in Intent.pulses for this action. */
  readonly id: string;
  apply(ctx: ActionContext): void;
}

/**
 * Token-bucket rate limiter shared by all pulse-driven actions. Refills at
 * `maxPerSec` up to `burst`, consumes as many queued pulses as tokens allow,
 * and clamps the queue to `burst` so input can never be banked indefinitely.
 * This is what enforces the click-rate cap (and is the first anti-autoclicker
 * guard). Returns the amount actually consumed (may be fractional).
 */
export function consumePulses(
  entity: Entity,
  actionId: string,
  dt: number,
  maxPerSec: number,
  burst: number,
): number {
  const tokens = Math.min(burst, (entity.rateTokens[actionId] ?? burst) + maxPerSec * dt);
  const queued = Math.min(entity.intent.pulses[actionId] ?? 0, burst);
  const consumed = Math.min(queued, tokens);
  entity.rateTokens[actionId] = tokens - consumed;
  entity.intent.pulses[actionId] = queued - consumed;
  return consumed;
}
